import { Injectable, inject, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  InAppBrowser,
  DismissStyle,
  iOSViewStyle,
  iOSAnimation,
  AndroidViewStyle,
  AndroidAnimation,
} from '@capacitor/inappbrowser';
import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { StatusBar, Style, Animation } from '@capacitor/status-bar';
import { Device } from '@capacitor/device';
import { Subject, type Observable } from 'rxjs';
import { LoggingService } from './logging.service';

/**
 * Result returned when digital payment browser flow completes
 */
export interface DigitalPaymentBrowserResult {
  /** Whether the payment was successful */
  success: boolean;
  /** Payment method token from Paymentus */
  token?: string;
  /** Payment type code (GP, AP, PAYPAL_ACCOUNT, VENMO, etc.) */
  type?: string;
  /** Masked account number */
  maskedAccountNumber?: string;
  /** Cardholder name */
  cardHolderName?: string;
  /** Error message if failed */
  error?: string;
  /** Whether user cancelled the flow */
  cancelled?: boolean;
}

/**
 * Configuration for opening digital payment browser
 */
export interface DigitalPaymentBrowserConfig {
  /** The Paymentus URL to open */
  url: string;
  /** Payment account type for display purposes */
  paymentAccountType: string;
  /** Callback URL pattern to detect (e.g., '/digital-payment-callback') */
  callbackUrlPattern: string;
}

/**
 * Service to handle digital payment flows in InAppBrowser for iOS/Android.
 *
 * This service manages the lifecycle of opening Paymentus payment pages,
 * detecting callback URLs, extracting payment tokens, and closing the browser.
 *
 * @example
 * ```typescript
 * const result = await this.digitalPaymentBrowser.openPaymentBrowser({
 *   url: paymentusUrl,
 *   paymentAccountType: 'Google Pay',
 *   callbackUrlPattern: '/digital-payment-callback'
 * });
 *
 * if (result.success && result.token) {
 *   // Handle successful payment
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DigitalPaymentBrowserService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);
  private readonly loggingService = inject(LoggingService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly isNativePlatform = this.isBrowser && Capacitor.isNativePlatform();

  private browserListeners: PluginListenerHandle[] = [];
  private resultSubject: Subject<DigitalPaymentBrowserResult> | null = null;
  private closingFromCallback = false;
  private callbackDetected = false;
  private resultEmitted = false;
  private pendingCancelTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly preBrowserStatusBarStyle: Style | null = Style.Dark;

  /** Default callback URL pattern to detect */
  static readonly DEFAULT_CALLBACK_PATTERN = '/digital-payment-callback';

  /**
   * Check if the current platform supports InAppBrowser (iOS/Android native)
   */
  isInAppBrowserSupported(): boolean {
    return this.isNativePlatform;
  }

  /**
   * Opens the Paymentus payment URL in an InAppBrowser WebView.
   * Returns a promise that resolves when the payment flow completes.
   *
   * @param config Configuration for the browser
   * @returns Promise with the payment result
   */
  async openPaymentBrowser(config: DigitalPaymentBrowserConfig): Promise<DigitalPaymentBrowserResult> {
    if (!this.isNativePlatform) {
      return {
        success: false,
        error: 'InAppBrowser is only supported on native platforms (iOS/Android)',
      };
    }

    // Clean up any existing listeners
    await this.removeAllBrowserListeners();
    this.closingFromCallback = false;
    this.callbackDetected = false;
    this.resultEmitted = false;
    this.clearPendingCancelTimer();

    return await new Promise<DigitalPaymentBrowserResult>((resolve) => {
      this.resultSubject = new Subject<DigitalPaymentBrowserResult>();

      // Subscribe to result
      const subscription = this.resultSubject.subscribe((result) => {
        subscription.unsubscribe();
        this.resultSubject = null;
        resolve(result);
      });

      // Start the browser flow
      void this.startBrowserFlow(config);
    });
  }

  /**
   * Opens the payment browser and returns an observable for the result.
   * Useful when you need to handle the result reactively.
   *
   * @param config Configuration for the browser
   * @returns Observable that emits once with the payment result
   */
  openPaymentBrowser$(config: DigitalPaymentBrowserConfig): Observable<DigitalPaymentBrowserResult> {
    const subject = new Subject<DigitalPaymentBrowserResult>();

    void this.openPaymentBrowser(config).then((result) => {
      subject.next(result);
      subject.complete();
    });

    return subject.asObservable();
  }

  /**
   * Manually close the browser if open
   */
  async closeBrowser(): Promise<void> {
    try {
      await InAppBrowser.close();
    } catch {
      // Browser might already be closed
    }
    await this.removeAllBrowserListeners();
  }

  /**
   * Parse callback URL to extract payment data
   */
  parseCallbackUrl(url: string): DigitalPaymentBrowserResult {
    try {
      const urlObj = new URL(url);
      const params = urlObj.searchParams;

      const token = params.get('token') ?? params.get('Token');
      const type = params.get('type') ?? params.get('Type');
      const status = params.get('status');
      const maskedAccountNumber = params.get('maskedAccountNumber') ?? params.get('MaskedAccountNumber');
      const cardHolderName = params.get('cardHolderName') ?? params.get('CardHolderName');
      const error = params.get('error');

      // Check for explicit error
      if (error !== null || status === 'error' || status === 'failed') {
        return {
          success: false,
          error: error ?? 'Payment failed',
        };
      }

      // Check for successful token
      if (token) {
        return {
          success: true,
          token: decodeURIComponent(token),
          type: type ? decodeURIComponent(type) : undefined,
          maskedAccountNumber: maskedAccountNumber ? decodeURIComponent(maskedAccountNumber) : undefined,
          cardHolderName: cardHolderName ? decodeURIComponent(cardHolderName) : undefined,
        };
      }

      // Check for cancellation
      if (status === 'cancelled' || status === 'canceled') {
        return {
          success: false,
          cancelled: true,
        };
      }

      // Unknown state
      return {
        success: false,
        error: 'Unable to parse payment response',
      };
    } catch {
      return {
        success: false,
        error: 'Invalid callback URL format',
      };
    }
  }

  private async startBrowserFlow(config: DigitalPaymentBrowserConfig): Promise<void> {
    try {
      // Set up browser close listener
      const closeListener = await InAppBrowser.addListener('browserClosed', () => {
        this.ngZone.run(() => {
          void this.restoreStatusBar();

          if (this.closingFromCallback) {
            this.closingFromCallback = false;
            return;
          }

          // User closed browser manually - treat as cancellation
          this.scheduleCancellationResult();
        });
      });
      this.browserListeners.push(closeListener);

      // Set up deep link listener to detect callback URL
      // System browser (SFSafariViewController/Chrome Custom Tabs) doesn't fire navigation events,
      // so we use deep links instead. The backend redirects to a URL scheme like:
      // gassouth://digital-payment-callback?token=xxx&type=GP
      const appUrlListener = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.ngZone.run(() => {
          this.handleDeepLink(event.url, config.callbackUrlPattern);
        });
      });
      this.browserListeners.push(appUrlListener);

      // Open the payment URL in system browser (SFSafariViewController on iOS, Chrome Custom Tabs on Android)
      // Using openInSystemBrowser because:
      // 1. Google Pay requires system browser - it does not work in WebViews
      // 2. Apple Pay also requires system browser capabilities
      // 3. SFSafariViewController/Chrome Custom Tabs still allow detecting when browser closes
      await InAppBrowser.openInSystemBrowser({
        url: config.url,
        options: {
          android: {
            showTitle: true,
            hideToolbarOnScroll: false,
            viewStyle: AndroidViewStyle.FULL_SCREEN,
            startAnimation: AndroidAnimation.FADE_IN,
            exitAnimation: AndroidAnimation.FADE_OUT,
          },
          iOS: {
            closeButtonText: DismissStyle.DONE,
            viewStyle: iOSViewStyle.FULL_SCREEN,
            animationEffect: iOSAnimation.COVER_VERTICAL,
            enableBarsCollapsing: true,
            enableReadersMode: false,
          },
        },
      });
    } catch (error) {
      this.emitResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to open payment browser',
      });
    }
  }

  /**
   * Handle deep link callback from the system browser.
   * The backend redirects to a URL scheme like: gassouth://digital-payment-callback?token=xxx
   */
  private handleDeepLink(url: string | undefined, callbackPattern: string): void {
    if (!url) {
      return;
    }

    // Check if this is a callback URL for either:
    // - HTTPS app links: https://host/digital-payment-callback?...
    // - Custom scheme fallback: gassouth://digital-payment-callback?...
    if (this.isCallbackUrl(url, callbackPattern)) {
      const result = this.parseCallbackUrl(url);
      const callbackPath = this.getCallbackPathForLog(url);

      this.loggingService.logInfo(`Digital payment callback detected on path: ${callbackPath}`);
      this.callbackDetected = true;
      this.clearPendingCancelTimer();
      this.closingFromCallback = true;

      // Close browser and emit result
      void this.closeBrowser()
        .then(() => {
          this.loggingService.logInfo(
            `Digital payment callback resolved with outcome: ${result.success ? 'success' : result.cancelled ? 'cancelled' : 'error'}`,
          );
          this.emitResult(result);
        })
        .catch(() => {
          this.closingFromCallback = false;
          this.loggingService.logInfo(
            `Digital payment callback resolved with outcome: ${result.success ? 'success' : result.cancelled ? 'cancelled' : 'error'}`,
          );
          this.emitResult(result);
        })
        .finally(() => {
          void this.restoreStatusBar();
        });
    }
  }

  private isCallbackUrl(url: string, callbackPattern: string): boolean {
    const normalizedPattern = callbackPattern.startsWith('/')
      ? callbackPattern.toLowerCase()
      : `/${callbackPattern.toLowerCase()}`;

    try {
      const parsedUrl = new URL(url);
      const callbackPath = this.extractCallbackPath(parsedUrl).toLowerCase();
      return callbackPath === normalizedPattern;
    } catch {
      return url.toLowerCase().includes(normalizedPattern);
    }
  }

  private extractCallbackPath(parsedUrl: URL): string {
    if (parsedUrl.pathname && parsedUrl.pathname !== '/') {
      return parsedUrl.pathname;
    }

    if (parsedUrl.host) {
      return `/${parsedUrl.host}`;
    }

    return '/';
  }

  private getCallbackPathForLog(url: string): string {
    try {
      const parsedUrl = new URL(url);
      return this.extractCallbackPath(parsedUrl);
    } catch {
      return 'unknown';
    }
  }

  private emitResult(result: DigitalPaymentBrowserResult): void {
    if (this.resultEmitted) {
      return;
    }

    this.resultEmitted = true;
    this.closingFromCallback = false;
    this.clearPendingCancelTimer();
    if (this.resultSubject) {
      this.resultSubject.next(result);
      this.resultSubject.complete();
    }
    void this.removeAllBrowserListeners();
  }

  private scheduleCancellationResult(): void {
    if (this.resultEmitted || !this.resultSubject) {
      return;
    }

    this.clearPendingCancelTimer();
    this.pendingCancelTimer = setTimeout(() => {
      this.pendingCancelTimer = null;
      if (this.resultEmitted || this.callbackDetected || !this.resultSubject) {
        return;
      }

      this.emitResult({
        success: false,
        cancelled: true,
      });
    }, 400);
  }

  private clearPendingCancelTimer(): void {
    if (this.pendingCancelTimer !== null) {
      clearTimeout(this.pendingCancelTimer);
      this.pendingCancelTimer = null;
    }
  }

  private async removeAllBrowserListeners(): Promise<void> {
    this.clearPendingCancelTimer();
    for (const listener of this.browserListeners) {
      try {
        await listener.remove();
      } catch {
        // Listener might already be removed
      }
    }
    this.browserListeners = [];
  }

  /**
   * Restore iOS status bar to default state after browser closes.
   * Restores: DARK style, #000 background, overlaysWebView false, visible true.
   * This is a non-fatal operation guarded for iOS/native context.
   */
  private async restoreStatusBar(): Promise<void> {
    if (!this.isNativePlatform) {
      return;
    }

    try {
      const isIos = await this.isIosNative();
      if (!isIos) {
        return;
      }

      const preferredStyle = this.preBrowserStatusBarStyle ?? Style.Light;
      // Apply the critical iOS properties first.
      // setBackgroundColor is Android-only and can throw on iOS, so do not call it here.
      StatusBar.hide({ animation: Animation.None })
        .then(async () => {
          await StatusBar.show({ animation: Animation.None });
        })
        .catch((error) => {
          // Non-fatal: log but don't interrupt flow
          this.loggingService.logInfo(
            `[StatusBar-Restore] Failed to hide/show status bar (non-fatal): ${error instanceof Error ? error.message : 'unknown'}`,
          );
        });
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setStyle({ style: preferredStyle });

      // Retry once after close transition completes; iOS can re-apply system defaults briefly.
      setTimeout(() => {
        void StatusBar.setOverlaysWebView({ overlay: false });
        void StatusBar.setStyle({ style: preferredStyle });
      }, 120);

      this.loggingService.logInfo(
        `[StatusBar-Restore] Successfully restored iOS status bar: style=${preferredStyle}, non-overlay`,
      );
    } catch (error) {
      // Non-fatal: log but don't interrupt flow
      this.loggingService.logInfo(
        `[StatusBar-Restore] Failed to restore status bar (non-fatal): ${error instanceof Error ? error.message : 'unknown'}`,
      );
    }
  }

  /**
   * Check if running on iOS native platform
   */
  private async isIosNative(): Promise<boolean> {
    if (!this.isNativePlatform) {
      return false;
    }

    try {
      const info = await Device.getInfo();
      return info.platform === 'ios';
    } catch {
      // If we can't determine platform, assume not iOS
      return false;
    }
  }
}
